/**
 * Toast — dark pill confirmations, bottom-centred, ~1.6s auto-dismiss
 * (spec.md § Editor / Toast confirmations, § UI & design / Micro-interactions).
 *
 * Used by the editor for: copy markdown / copy link / delete success, and the
 * non-409 save error. Slice C composes the same provider for its own copy/
 * delete actions, so the provider + `useToast()` hook live here as the shared
 * surface.
 *
 * Design tokens only (no hard-coded colors): the pill uses the `primary`
 * ink fill with `primary-fg` text, matching the dark-pill spec in both themes.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import "./animations.css";

/** Auto-dismiss duration in ms (spec: ~1.6s). */
export const TOAST_DURATION_MS = 1600;

interface ToastState {
  id: number;
  message: string;
}

interface ToastContextValue {
  /** Show a dark-pill toast; auto-dismisses after ~1.6s. */
  showToast: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Hook to fire a toast. Must be rendered under a {@link ToastProvider}.
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a <ToastProvider>");
  }
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idRef = useRef(0);

  const showToast = useCallback((message: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    idRef.current += 1;
    setToast({ id: idRef.current, message });
    timerRef.current = setTimeout(() => {
      setToast(null);
      timerRef.current = null;
    }, TOAST_DURATION_MS);
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toast={toast} />
    </ToastContext.Provider>
  );
}

function ToastViewport({ toast }: { toast: ToastState | null }): ReactNode {
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed inset-x-0 bottom-6 z-50 flex justify-center"
    >
      {toast ? (
        <div
          key={toast.id}
          role="status"
          data-testid="toast"
          className="motion-safe:animate-[toast-in_0.18s_ease] pointer-events-auto rounded-[11px] bg-primary px-4 py-2.5 font-mono text-[13px] font-medium text-primary-fg shadow-lg"
        >
          {toast.message}
        </div>
      ) : null}
    </div>
  );
}
